import React, { useEffect, useMemo } from 'react';
import _get from 'lodash/get';
import { Documentation } from 'graphql-common';
import { useEdmDocumentationsQuery } from '@lib/graphql-common';
import { Option } from '@lib/interfaces/form';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import FormTemplateFieldNames from '@lib/enums/fieldNames/formTemplateFieldNames';

export default function useDocumentationLinksPrefill({
  formRef,
  formTemplateId,
  initialDocumentationLinksRef,
  skip = false,
}: {
  formRef: FormRefType;
  formTemplateId?: string;
  initialDocumentationLinksRef?: React.MutableRefObject<Option[]>;
  skip?: boolean;
}) {
  const { data, loading } = useEdmDocumentationsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      filters: [
        {
          linkedFormTemplatesId: {
            predicate: { eq: formTemplateId || '' },
          },
        },
      ],
    },
    skip: !formTemplateId || skip,
  });

  const initialDocumentationLinks: Option[] = useMemo(() => {
    const collection = _get(data, 'data.collection', []) as Documentation[];
    return collection.map((doc) => ({
      value: doc.id,
      label: `${doc.code ? `${doc.code} ` : ''}${doc.name}`,
    }));
  }, [data]);

  useEffect(() => {
    if (!formTemplateId || !data) return;
    if (initialDocumentationLinksRef) {
      // eslint-disable-next-line no-param-reassign
      initialDocumentationLinksRef.current = initialDocumentationLinks;
    }
    if (formRef.current) {
      formRef.current.reset({
        ...formRef.current.values,
        [FormTemplateFieldNames.DocumentationLinks]: initialDocumentationLinks,
      });
    }
  }, [
    formTemplateId,
    data,
    formRef,
    initialDocumentationLinks,
    initialDocumentationLinksRef,
  ]);

  return { loading: Boolean(formTemplateId) && !skip && (loading || !data) };
}
